import { useState, useEffect, useRef, useCallback } from 'react';
import { MagnifyingGlass, X, Plus, User, MapPin, Buildings } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

export function CustomerPicker({ value, onSelect, onClear, placeholder = 'Search customers...', disabled = false }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const containerRef = useRef(null);
    const searchTimer = useRef(null);

    const search = useCallback(async (q) => {
        if (!q || q.length < 2) { setResults([]); return; }
        setLoading(true);
        try {
            const { customerTypeahead } = await import('../lib/api');
            const res = await customerTypeahead(q);
            setResults(res.data.results || []);
        } catch { setResults([]); }
        finally { setLoading(false); }
    }, []);

    const handleInputChange = (val) => {
        setQuery(val);
        setOpen(true);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => search(val), 300);
    };

    const handleSelect = (customer) => {
        onSelect(customer);
        setQuery('');
        setResults([]);
        setOpen(false);
    };

    const handleClear = () => {
        onClear();
        setQuery('');
        setResults([]);
    };

    const handleCreateNew = () => {
        setOpen(false);
        setShowCreate(true);
    };

    const handleCustomerCreated = (newCustomer) => {
        setShowCreate(false);
        onSelect(newCustomer);
        setQuery('');
        setResults([]);
    };

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const primaryContact = (customer) => {
        const contacts = customer.contacts || [];
        return contacts.find(c => c.is_primary) || contacts[0];
    };

    // If a customer is selected, show the chip
    if (value) {
        return (
            <>
                <div className="flex items-center gap-2 min-h-[38px] border rounded-md px-3 py-1.5 bg-card" data-testid="customer-picker-selected">
                    <Buildings size={14} className="text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium truncate">{value.company_name}</span>
                        {value.country && <span className="text-xs text-muted-foreground ml-1.5">({value.country})</span>}
                    </div>
                    {!disabled && (
                        <button
                            onClick={handleClear}
                            className="text-muted-foreground hover:text-foreground flex-shrink-0"
                            data-testid="customer-picker-clear"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {showCreate && (
                    <QuickCreateCustomerModal
                        onCreated={handleCustomerCreated}
                        onClose={() => setShowCreate(false)}
                        initialName={query}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div ref={containerRef} className="relative" data-testid="customer-picker">
                <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={query}
                        onChange={e => handleInputChange(e.target.value)}
                        onFocus={() => { if (query.length >= 2) setOpen(true); }}
                        placeholder={placeholder}
                        className="pl-8 h-9"
                        disabled={disabled}
                        data-testid="customer-picker-input"
                    />
                    {query && (
                        <button className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => { setQuery(''); setResults([]); setOpen(false); }}>
                            <X size={12} />
                        </button>
                    )}
                </div>

                {open && (query.length >= 2) && (
                    <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="customer-picker-dropdown">
                        {loading && (
                            <div className="px-3 py-2.5 text-xs text-muted-foreground">Searching...</div>
                        )}

                        {!loading && results.length === 0 && (
                            <div className="px-3 py-2.5 text-xs text-muted-foreground">
                                No customers found for "{query}"
                            </div>
                        )}

                        {!loading && results.map(customer => {
                            const pc = primaryContact(customer);
                            return (
                                <button
                                    key={customer.id}
                                    className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-b-0"
                                    onClick={() => handleSelect(customer)}
                                    data-testid={`customer-option-${customer.id}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Buildings size={13} className="text-muted-foreground flex-shrink-0" />
                                        <span className="text-sm font-medium truncate">{customer.company_name}</span>
                                        {customer.country && (
                                            <Badge variant="outline" className="text-[10px] h-4 flex-shrink-0">{customer.country}</Badge>
                                        )}
                                    </div>
                                    {pc && (
                                        <div className="flex items-center gap-3 mt-0.5 ml-5 text-[11px] text-muted-foreground">
                                            {pc.name && <span className="flex items-center gap-1"><User size={10} />{pc.name}</span>}
                                            {pc.email && <span>{pc.email}</span>}
                                        </div>
                                    )}
                                    {customer.default_currency && customer.default_currency !== 'EUR' && (
                                        <div className="ml-5 mt-0.5">
                                            <Badge variant="secondary" className="text-[10px] h-4">{customer.default_currency}</Badge>
                                        </div>
                                    )}
                                </button>
                            );
                        })}

                        {/* Create new customer option */}
                        <button
                            className="w-full text-left px-3 py-2.5 hover:bg-primary/5 transition-colors flex items-center gap-2 text-primary border-t border-border"
                            onClick={handleCreateNew}
                            data-testid="customer-picker-create-new"
                        >
                            <Plus size={14} weight="bold" />
                            <span className="text-sm font-medium">Create new customer</span>
                            {query && <span className="text-xs text-muted-foreground">"{query}"</span>}
                        </button>
                    </div>
                )}
            </div>

            {showCreate && (
                <QuickCreateCustomerModal
                    onCreated={handleCustomerCreated}
                    onClose={() => setShowCreate(false)}
                    initialName={query}
                />
            )}
        </>
    );
}

function QuickCreateCustomerModal({ onCreated, onClose, initialName = '' }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        company_name: initialName,
        country: '',
        default_currency: 'EUR',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
    });

    const handleSubmit = async () => {
        if (!form.company_name.trim()) return;
        setSaving(true);
        try {
            const { createCustomer } = await import('../lib/api');
            const contacts = [];
            if (form.contact_name || form.contact_email || form.contact_phone) {
                contacts.push({
                    name: form.contact_name,
                    email: form.contact_email,
                    phone: form.contact_phone,
                    role: 'Buyer',
                    is_primary: true,
                });
            }
            const res = await createCustomer({
                company_name: form.company_name.trim(),
                country: form.country,
                default_currency: form.default_currency,
                contacts,
            });
            onCreated(res.data);
        } catch (e) {
            const { toast } = await import('sonner');
            toast.error(e.response?.data?.detail || 'Failed to create customer');
        } finally { setSaving(false); }
    };

    const onChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-5 space-y-4 z-[101]" data-testid="quick-create-customer-modal">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-base">Quick Create Customer</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Company Name *</label>
                        <Input name="company_name" value={form.company_name} onChange={onChange} autoFocus data-testid="qc-company-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Country</label>
                            <Input name="country" value={form.country} onChange={onChange} placeholder="e.g. Netherlands" data-testid="qc-country" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Currency</label>
                            <select name="default_currency" value={form.default_currency} onChange={onChange} className="w-full h-9 border rounded-md px-3 text-sm bg-background" data-testid="qc-currency">
                                <option value="EUR">EUR</option>
                                <option value="USD">USD</option>
                                <option value="GBP">GBP</option>
                                <option value="INR">INR</option>
                                <option value="AED">AED</option>
                            </select>
                        </div>
                    </div>

                    <div className="border-t border-border pt-3">
                        <label className="text-xs text-muted-foreground mb-2 block font-medium">Primary Contact (optional)</label>
                        <div className="grid grid-cols-1 gap-2">
                            <Input name="contact_name" value={form.contact_name} onChange={onChange} placeholder="Contact name" data-testid="qc-contact-name" />
                            <div className="grid grid-cols-2 gap-2">
                                <Input name="contact_email" value={form.contact_email} onChange={onChange} placeholder="Email" data-testid="qc-contact-email" />
                                <Input name="contact_phone" value={form.contact_phone} onChange={onChange} placeholder="Phone" data-testid="qc-contact-phone" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded-md hover:bg-muted/50">Cancel</button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving || !form.company_name.trim()}
                        className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                        data-testid="qc-submit"
                    >
                        {saving ? 'Creating...' : 'Create & Select'}
                    </button>
                </div>
            </div>
        </div>
    );
}
